import { addNotification } from '~/cynqueModules/addNotification'

/**
 * Surface unhandled Vue errors as a notification, non-destructively.
 *
 * WHY THIS EXISTS: Nuxt 2 installed a global `Vue.config.errorHandler` that
 * showed the full error page for ANY Vue error except render-function errors
 * (see `.nuxt/client.js` in the Bridge worktree). Nuxt 4 installs no equivalent,
 * so an error thrown in a click handler or lifecycle hook changed NOTHING on
 * screen — it went to the console and Honeybadger and nowhere else. That is how
 * a broken "create Company" reached a user as "the button just doesn't work".
 *
 * WHY A NOTIFICATION RATHER THAN THE ERROR PAGE (Jerry's call, 2026-07-27):
 * the Nuxt-2 behaviour is destructive. These errors are usually thrown from
 * inside a dialog, and replacing the whole app with the error page throws away
 * whatever the user had typed. A notification makes the failure visible while
 * leaving the dialog — and the user's input — intact. The error page is still
 * used for the asyncData path, which calls showError() explicitly.
 *
 * `vue:error` is ADDITIVE — Nuxt calls it from its own errorHandler — so console
 * logging and Honeybadger reporting continue unchanged. Do NOT replace
 * `vueApp.config.errorHandler` here; that would take over from Nuxt.
 */

/** Collapse repeats: one render can throw the same error on every tick. */
const DEDUPE_WINDOW_MS = 5000

export default defineNuxtPlugin((nuxtApp) => {
  const lastSeenAt = new Map<string, number>()

  // Params are annotated because Nuxt's auto-import types are not visible to
  // vue-tsc here (the root tsconfig excludes .nuxt and overrides `paths`), so an
  // unannotated callback param is an implicit-any error. Every plugin in this
  // directory carries the same unavoidable `defineNuxtPlugin`/`nuxtApp` pair;
  // annotating the rest keeps this file's share of it to the minimum.
  nuxtApp.hook('vue:error', (error: unknown, _instance: any, info: string) => {
    try {
      const message =
        (error as Error)?.message ?? (typeof error === 'string' ? error : '')
      const key = `${message}::${info}`
      const now = Date.now()

      const previous = lastSeenAt.get(key)
      if (previous !== undefined && now - previous < DEDUPE_WINDOW_MS) {
        return
      }
      lastSeenAt.set(key, now)

      addNotification({
        type: 'error',
        message: message
          ? `Something went wrong: ${message}`
          : 'Something went wrong.',
      })
    } catch (e) {
      // Never let the error reporter become the error. Nuxt has already logged
      // the original by the time this hook runs.
      // eslint-disable-next-line no-console
      console.error('[plugins/05.vueErrorNotifier] failed to notify:', e)
    }
  })
})
